/**
 * ReportRenderPage.js — standalone report render route for headless Chromium.
 *
 * URL: /report-render?code=<PCON24CD>&type=<reportType>
 *   code  — constituency code e.g. E14000770  (required)
 *   type  — default | business | client | motability | waspi  (default: "default")
 *   name  — optional; if absent the route resolves it from the FGB geo file
 *
 * Identity resolution (the core fix):
 *   MPCard uses loadFullMpData(constName) which calls parliament.uk / TheyWorkForYou
 *   by CONSTITUENCY NAME, not by code. Passing the code as the name breaks every
 *   name-dependent lookup. The route must resolve code → PCON24NM from the same
 *   FGB geo file the live app uses, then pass the real name to components.
 *   loadGeo (utils/mapPage/loadGeo.js) is reused here — it has module-level LRU
 *   caching and in-flight dedup so the FGB is downloaded at most once per session.
 *
 * Render-complete signal:
 *   DOM polls for Recharts SVG content appearing (charts rendered with data).
 *   Hard 25-second timeout fallback so Playwright never hangs.
 */

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ConstituencyReportView from "./ConstituencyReportView";
import BusinessReportView     from "./BusinessReportView";
import HolidayTaxReportView   from "./HolidayTaxReportView";
import MotabilityReportView   from "./MotabilityReportView";
import WaspiReportView        from "./WaspiReportView";
import QuestionDrilldownView  from "./QuestionDrilldownView";
import { loadGeo }             from "./utils/mapPage/loadGeo";
import { normalizeId }         from "./utils/mapPage/helpers";
import { loadGroupData, fetchConstituencyGroup } from "./utils/metricsClient";

const REPORT_WIDTH_PX = 840;
const MAX_WAIT_MS     = 25000;

// Analyst-note block for adapted templates (mirrors ReportPage's NotesBlock).
function NotesBlock({ text, position = "bottom" }) {
  if (!text) return null;
  return (
    <div style={{
      flexShrink: 0,
      margin: position === "top" ? "0 0 10px" : "10px 0 0",
      padding: "12px 16px",
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderLeft: "4px solid #0f172a",
      borderRadius: 6,
      fontSize: 12.5,
      lineHeight: 1.5,
      color: "#0f172a",
      whiteSpace: "pre-wrap",
    }}>
      <div style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 4 }}>
        Analyst note
      </div>
      {text}
    </div>
  );
}

// The loadGroupData calls each component makes internally — used to piggyback
// on fetchMetrics inflight dedup so we await the exact same promises.
// Component effects fire before parent effects (children-first), so by the time
// this effect runs the component has already started its fetches (inflight.set).
// Our loadGroupData calls find those promises via the same key and share them —
// no duplicate requests. When they resolve, the component has called setCharts
// and data is in state; we add a paint buffer then signal complete.
const FETCH_GROUPS = {
  default: [
    { groupId: "vote_intention",   theme: undefined },
    { groupId: "national_issues",  theme: undefined },
    { groupId: "priorities",       theme: undefined },
    { groupId: "best_pm",          theme: undefined },
  ],
  business: [
    { groupId: "local_business_challenge", theme: undefined },
    { groupId: "vote_intention",           theme: undefined },
    { groupId: "sector_importance",        theme: "ind" },
    { groupId: "trust",                    theme: "ind" },
    { groupId: "trust_drivers",            theme: "ind" },
  ],
  // For client/motability/waspi: gate on vote_intention as the minimum present signal.
  client:    [{ groupId: "vote_intention", theme: undefined }],
  motability:[{ groupId: "vote_intention", theme: undefined }],
  waspi:     [{ groupId: "vote_intention", theme: undefined }],
};

export default function ReportRenderPage() {
  const [searchParams] = useSearchParams();

  const code      = searchParams.get("code") || "";
  const typeParam = searchParams.get("type") || "default";
  const nameParam = searchParams.get("name") || ""; // hint; resolved from geo if absent
  const pdfToken  = searchParams.get("pdf_token") || ""; // scoped HMAC token from /api/report/pdf/

  // ── Scope + custom-report params ──────────────────────────────────────────
  const national     = (searchParams.get("scope") || "constituency") === "national";
  const drillGroup   = searchParams.get("group") || "";
  const drillOpts    = (searchParams.get("opts") || "").split(",").map(s => s.trim()).filter(Boolean);
  const heroGroupId  = searchParams.get("hero") || "vote_intention";
  const notesText    = searchParams.get("notes") || "";
  const notesPos     = searchParams.get("notes_pos") || "bottom";
  const isDrilldown  = typeParam === "drilldown";
  const hasNotes     = !!notesText.trim();
  // Adapt = a base template carrying a note and/or a (political) hero swap.
  const isExtend     = !isDrilldown && (hasNotes || (typeParam === "default" && heroGroupId !== "vote_intention"));
  const displayName  = national ? "Great Britain" : (nameParam || "");

  // ── Token validation (Step 0) ────────────────────────────────────────────
  // When ?pdf_token is present, validate it via /api/report/validate-token/
  // before rendering anything. If invalid, show an error and never signal
  // data-render-complete — Playwright times out cleanly with a clear message.
  // When no token is present (direct browser testing / dev), skip validation.
  // The token authorises ONE render job (this code + type, ≤90s).
  const [tokenValid, setTokenValid] = useState(!pdfToken); // true if no token (dev bypass)

  useEffect(() => {
    if (!pdfToken) return; // no token → already valid (dev mode)
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/report/validate-token/", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ token: pdfToken, code, type: typeParam }),
        });
        const json = await res.json();
        if (!cancelled) setTokenValid(json.valid === true);
      } catch (err) {
        console.error("[ReportRenderPage] token validation error:", err);
        if (!cancelled) setTokenValid(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pdfToken, code, typeParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Step 1: resolve code → constituency name from the FGB geo file ────────
  // loadGeo reuses the same module-level LRU cache as MobileMap — FGB is
  // downloaded at most once. We wait for the resolved name before mounting the
  // report components so every name-dependent sub-fetch (MPCard, header) is correct.
  const [resolvedName,   setResolvedName]   = useState(national ? "Great Britain" : (nameParam || null));
  const [extDataBlurb,   setExtDataBlurb]   = useState(null);
  const [identityReady,  setIdentityReady]  = useState(national || !!nameParam);
  const [renderComplete, setRenderComplete] = useState(false);
  const [invalidCode,    setInvalidCode]    = useState(false);

  useEffect(() => {
    if (!code) return;

    // National scope has no single constituency — no geo/MP resolution needed.
    if (national) {
      setResolvedName("Great Britain");
      setIdentityReady(true);
      return;
    }

    // If the caller already supplied a name, skip the geo lookup.
    if (nameParam) {
      setResolvedName(nameParam);
      setIdentityReady(true);
      return;
    }

    let cancelled = false;

    loadGeo({
      level: "constituency",
      constituencyCode: null,   // load all constituencies (cached after first call)
      doFit: false,
      updateAreas: false,
      setState: (updaterOrObj) => {
        const updates = typeof updaterOrObj === "function" ? updaterOrObj({}) : updaterOrObj;
        if (updates?.geoData?.features && !cancelled) {
          const feature = updates.geoData.features.find(
            f => f.properties?.PCON24CD === code || f.properties?.area_cd === code
          );
          if (!feature) {
            // Code not found in the geo file — signal complete immediately so
            // Playwright doesn't hang, then show a clear error rather than a
            // half-empty report with the raw code as the constituency name.
            setInvalidCode(true);
            setIdentityReady(true);
            setRenderComplete(true);
            return;
          }
          const name = feature.properties.PCON24NM || code;
          setResolvedName(name);
          setIdentityReady(true);
        }
      },
      mapRef:                   { current: null },
      fitToGeo:                 () => {},
      normalizeId,
      handlePossibleAreasChange: () => {},
    });

    return () => { cancelled = true; };
  }, [code, nameParam]);

  // ── Step 2: fetch extDataBlurb for majority + MP info ────────────────────
  // Uses geoLevel:"ward" + constituencyCodes — matches MainPage.fetchExtData()
  // path for constituency selection, which is what returns mp_name / majority.
  useEffect(() => {
    if (!code || !identityReady || national) return; // national has no MP/majority
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/api/fetch_ext_data/", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ geoLevel: "ward", constituencyCodes: [code] }),
        });
        if (!res.ok) throw new Error(`fetch_ext_data ${res.status}`);
        const json = await res.json();
        if (!cancelled) setExtDataBlurb(Array.isArray(json) ? json[0] || {} : json || {});
      } catch (err) {
        console.warn("[ReportRenderPage] fetch_ext_data failed:", err);
        if (!cancelled) setExtDataBlurb({});
      }
    })();

    return () => { cancelled = true; };
  }, [code, identityReady]);

  // ── Step 3: data-promise render-complete signal ──────────────────────────
  //
  // Gates on the data layer, not SVG geometry.
  //
  // Mechanism: await the same loadGroupData calls the component makes internally.
  // fetchMetrics has a module-level `inflight` Map keyed by (level, areaCodes,
  // groupKeys, filters, theme). React fires children effects before parent effects,
  // so the component has already called loadGroupData and registered its promises
  // in `inflight` before this effect runs. Our loadGroupData calls find those
  // promises via identical keys and return them — zero extra network requests.
  // When all promises resolve, the component has called setCharts(..., loading:false)
  // and the data is in React state. We then add a fixed paint buffer for Recharts
  // to draw its bars, drain images (1.5s cap), and signal complete.
  //
  // 25s backstop remains as a safety net; with data-gating it should never fire.
  useEffect(() => {
    if (!identityReady) return;
    let cancelled = false;
    const started = Date.now();

    const maxWait = isDrilldown ? 32000 : MAX_WAIT_MS; // drill-downs do heavier async work
    const backstop = setTimeout(() => {
      if (!cancelled) {
        console.warn(`[ReportRenderPage] backstop fired at ${Math.round(Date.now() - started)}ms`);
        cancelled = true;
        setRenderComplete(true);
      }
    }, maxWait);

    (async () => {
      let reason = "data-loaded";

      try {
        if (isDrilldown) {
          // The drill-down loads its option data + the GB constituency dataset
          // (overall breakdown, map, top-constituencies). Warm both so the
          // component has settled before we buffer for paint.
          await Promise.all([
            loadGroupData({ groupId: drillGroup, areaCode: national ? "__NATIONAL__" : code }).catch(() => {}),
            fetchConstituencyGroup(drillGroup).catch(() => {}),
          ]);
        } else {
          const groups = FETCH_GROUPS[typeParam] || FETCH_GROUPS.default;
          // areaCode "GB" for national — components fetch the same, so this
          // piggybacks on fetchMetrics inflight dedup (no extra requests).
          await Promise.all(
            groups.map(({ groupId, theme }) =>
              loadGroupData({ groupId, areaCode: code, theme })
            )
          );
        }
      } catch (err) {
        // loadGroupData's internal catch resets loading:false even on error,
        // so the component has settled. We continue to signal complete.
        console.warn("[ReportRenderPage] loadGroupData error (continuing):", err?.message ?? err);
        reason = "data-error-settled";
      }

      if (cancelled) { clearTimeout(backstop); return; }

      // Drill-downs compute per-response top audiences (many filtered requests)
      // after the primary data lands — give that async work room before paint.
      if (isDrilldown) {
        await new Promise(r => setTimeout(r, 5000));
        if (cancelled) { clearTimeout(backstop); return; }
      }

      clearTimeout(backstop);

      // Paint buffer: Recharts bars + ward maps via ResizeObserver.
      // WardGroupStaticMapWithBase follows this sequence AFTER loadGroupData:
      //   ResizeObserver fires → mapUrl computed → <img> rendered → Mapbox fetch.
      // The ResizeObserver fires quickly but the Mapbox tile request is a slow
      // external network call. 3 500ms gives both ward maps time to receive their
      // Mapbox API responses and become complete before the image drain below.
      await new Promise(r => setTimeout(r, 3500));
      if (cancelled) return;

      // Poll until ALL <img> elements in the DOM are fully loaded (naturalWidth > 0),
      // or until 8s have passed. We poll rather than add listeners once, because
      // ward map <img> elements may not exist in the DOM when loadGroupData resolves
      // (they're added later when the ResizeObserver + mapUrl state update fires).
      const imgDrainDeadline = Date.now() + 8000;
      while (Date.now() < imgDrainDeadline) {
        if (cancelled) return;
        const imgs = Array.from(document.querySelectorAll("img"));
        const allDone = imgs.length > 0 &&
          imgs.every(img => img.complete && (img.naturalWidth > 0 || img.naturalWidth === undefined));
        if (allDone) break;
        await new Promise(r => setTimeout(r, 250));
      }

      if (cancelled) return;
      const elapsed = Math.round(Date.now() - started);
      console.log(`[ReportRenderPage] render-complete: reason="${reason}" elapsed=${elapsed}ms`);
      setRenderComplete(true);
    })();

    return () => { cancelled = true; clearTimeout(backstop); };
  }, [identityReady, code, typeParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Error / invalid states ───────────────────────────────────────────────
  if (!code) {
    return (
      <div style={{ padding: 40, fontFamily: "monospace", color: "#b91c1c", background: "#fff" }}>
        <p><strong>Missing required param: code</strong></p>
        <p>Usage: <code>/report-render?code=E14000770&amp;type=default</code></p>
        <p>type options: default | business | client | motability | waspi</p>
      </div>
    );
  }

  // Code not found in the constituency geo file — show a clear error.
  // data-render-complete is already "true" (set in the geo callback) so Playwright
  // exits cleanly rather than hanging to the 25s backstop.
  if (invalidCode) {
    return (
      <div
        data-render-complete="true"
        style={{ padding: 40, fontFamily: "monospace", color: "#b91c1c", background: "#fff" }}
      >
        <p><strong>Unknown or invalid constituency code: {code}</strong></p>
        <p>The code was not found in the constituency boundary file. Check the code is a current PCON24CD value (e.g. E14000001).</p>
        <p>type={typeParam}</p>
      </div>
    );
  }

  // Token present but failed validation → error state; data-render-complete
  // never flips so Playwright times out with a diagnostic message.
  if (tokenValid === false) {
    return (
      <div style={{ padding: 40, fontFamily: "monospace", color: "#b91c1c", background: "#fff" }}>
        <p><strong>Invalid or expired PDF render token.</strong></p>
        <p>Tokens are valid for 90 seconds and bound to a specific code + type.</p>
        <p>code={code} type={typeParam}</p>
      </div>
    );
  }

  // ── Template — only mount once token is validated AND identity is resolved ──
  // Gate on tokenValid so we don't start any fetches before the token check passes.
  const majority = extDataBlurb?.majority ?? null;

  // The base report for the current type/scope. When wrapped with a note the
  // report fills the remaining height (single-page squeeze), so pass height:100%.
  const baseReport = (reportHeight) => {
    switch (typeParam) {
      case "business":
        return <BusinessReportView constName={resolvedName} areaCode={code} national={national} height={reportHeight} />;
      case "client":
        return <HolidayTaxReportView constName={resolvedName} areaCode={code} majority={majority} national={national} height={reportHeight} />;
      case "motability":
        return <MotabilityReportView constName={resolvedName} areaCode={code} national={national} />;
      case "waspi":
        return <WaspiReportView constName={resolvedName} areaCode={code} majority={majority} national={national} height={reportHeight} />;
      default:
        return (
          <ConstituencyReportView
            constName={resolvedName}
            areaCode={code}
            mpName={extDataBlurb?.mp_name  ?? null}
            party={extDataBlurb?.mp_party  ?? null}
            roles={extDataBlurb?.mp_roles  ?? null}
            majority={majority}
            demographics={null}
            mapImages={null}
            national={national}
            heroGroupId={heroGroupId}
            height={reportHeight}
          />
        );
    }
  };

  const template = (!tokenValid || !identityReady) ? null : (() => {
    // Question drill-down
    if (isDrilldown) {
      return (
        <QuestionDrilldownView
          areaCode={code}
          constName={displayName}
          groupId={drillGroup}
          optionKeys={drillOpts.length ? drillOpts : null}
          national={national}
        />
      );
    }

    // Adapt (base template + analyst note and/or political hero swap). With a
    // note, wrap in a fixed A4 flex column so the report squeezes onto one page.
    if (isExtend && hasNotes) {
      return (
        <div style={{ height: "1123px", display: "flex", flexDirection: "column", boxSizing: "border-box" }}>
          {notesPos === "top" && <NotesBlock text={notesText} position="top" />}
          <div style={{ flex: 1, minHeight: 0 }}>{baseReport("100%")}</div>
          {notesPos !== "top" && <NotesBlock text={notesText} position="bottom" />}
        </div>
      );
    }

    // Standard base report (hero swap, if any, is applied inside baseReport).
    return baseReport(undefined);
  })();

  return (
    <div
      data-render-complete={renderComplete ? "true" : "false"}
      data-loading={!identityReady ? "true" : "false"}
      style={{
        width: REPORT_WIDTH_PX,
        margin: "0 auto",
        background: "#ffffff",
        boxSizing: "border-box",
        position: "relative",
      }}
    >
      {template || (
        <div style={{ height: "1123px", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 14, fontFamily: "sans-serif" }}>
          Resolving constituency…
        </div>
      )}

      {!renderComplete && identityReady && (
        <div style={{
          position: "absolute", inset: 0,
          background: "rgba(255,255,255,0.65)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, color: "#6b7280", fontFamily: "sans-serif",
          pointerEvents: "none", zIndex: 100,
        }}>
          Loading report for {resolvedName}…
        </div>
      )}
    </div>
  );
}
